import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import { DefaultResponse, DefaultResponseWithData } from "../models/system";
import {
  IOnboardingStatus,
  ISaveOnboardingCoverageRequest,
  ISaveOnboardingCoverageResponse,
} from "../models/Onboarding";

export default function useOnboarding(enabled = true) {
  const { get, post } = useApiHelper();
  const queryClient = useQueryClient();

  const status = useQuery({
    queryKey: ["onboarding"],
    queryFn: () => get<DefaultResponseWithData<IOnboardingStatus>>("/onboarding"),
    enabled,
    staleTime: 1000 * 30,
  });

  const saveCoverage = useMutation(
    (body: ISaveOnboardingCoverageRequest) =>
      post<ISaveOnboardingCoverageRequest, DefaultResponseWithData<ISaveOnboardingCoverageResponse>>(
        "/onboarding/coverage",
        body
      ),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["postcodeGroups"] });
        queryClient.invalidateQueries({ queryKey: ["onboarding"] });
      },
    }
  );

  const complete = useMutation(
    (skipped: boolean) =>
      post<{ skipped: boolean }, DefaultResponse>("/onboarding/complete", { skipped }),
    {
      onSuccess: () => {
        // Flip the cached flag straight away so the layout's redirect guard lets the user leave the wizard
        // without waiting for the refetch.
        queryClient.setQueryData<DefaultResponseWithData<IOnboardingStatus> | undefined>(["onboarding"], (old) =>
          old ? { ...old, data: { ...old.data, onboardingCompleted: true } } : old
        );
        queryClient.invalidateQueries({ queryKey: ["customer"] });
        queryClient.invalidateQueries({ queryKey: ["onboarding"] });
      },
    }
  );

  return { status, saveCoverage, complete };
}
